import React, { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@ideoz/ui';
import { hotEditStore, type HotEditBridgeApi } from '../../stores/hotEdit';
import type { FrameBox } from './usePreviewFrameBox';
import { pendingEdits } from '../../preview-bridge/pendingEdits';
import { GLASS, SURFACE } from './surface';

/** How long "Saved" stays before the bar goes. */
const SAVED_FOR_MS = 1400;

/**
 * The founder's unsaved changes, counted in one place (Editor Glass Pass follow-up).
 *
 * Apply used to sit in the toolbar beside the controls for one element, which made it read as
 * "apply this" when it applied everything. Now the changes gather in a bar along the bottom of
 * the preview - "6 changes" - with one Apply and one Discard, and the toolbar is only ever about
 * the element under it.
 */
export function PendingChangesBar({
  api,
  frame,
}: {
  api: HotEditBridgeApi | null;
  /** The preview's box, so the bar is centred on what the founder is editing. */
  frame?: FrameBox | null;
}): React.ReactElement | null {
  const pending = useStore(pendingEdits);
  const save = useStore(hotEditStore.save);
  const sheet = useStore(hotEditStore.dataGrid);
  const sheetHeight = useStore(hotEditStore.sheetHeight);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (save !== 'saved') return undefined;
    setShowSaved(true);
    const timer = window.setTimeout(() => setShowSaved(false), SAVED_FOR_MS);
    return () => window.clearTimeout(timer);
  }, [save]);

  const count = pending.length;
  const saving = save === 'saving';
  if (!api || (count === 0 && !saving && !showSaved)) return null;

  // Centred on the preview itself, not on whatever box the editor is mounted in, and above the
  // records sheet while that is open rather than lying across its rows. The centring is a track
  // the width of the preview rather than `translateX(-50%)`: the bar's entry animation ends on
  // `transform: none`, and a running animation beats an inline transform, so the bar drifted off
  // centre by half its own width (UI pass, issue 1).
  const bottom = (sheet ? sheetHeight + 16 : 16) + (frame ? Math.max(0, frame.top) : 0);
  const track = frame ? { left: frame.left, width: frame.width, bottom } : undefined;

  return (
    <div
      className={cn(
        'pointer-events-none absolute z-30 flex justify-center',
        !frame && 'inset-x-0 bottom-4'
      )}
      style={track}
    >
      <div
        className={cn(
          GLASS,
          SURFACE,
          'pointer-events-auto flex h-11 items-center gap-2 rounded-[14px] pl-3.5 pr-1.5'
        )}
        role="status"
        aria-live="polite"
        data-testid="hot-edit-pending-bar"
      >
        {count === 0 && !saving ? (
          <span className="flex items-center gap-1.5 pr-2 text-xs font-semibold">
            <Check size={14} style={{ color: 'var(--editor-accent)' }} />
            Saved to the prototype
          </span>
        ) : (
          <>
            <span className="text-xs font-semibold tabular-nums">
              {count} {count === 1 ? 'change' : 'changes'}
            </span>
            <span className="hidden text-[11px] sm:inline" style={{ color: 'var(--editor-ink-2)' }}>
              {save === 'failed'
                ? 'Something could not be saved.'
                : 'Showing in the preview, not saved yet.'}
            </span>
            <span className="mx-1 h-[18px] w-px bg-current opacity-[0.14]" aria-hidden />
            <button
              type="button"
              className="h-8 rounded-[10px] px-2.5 text-xs font-medium hover:bg-black/5 disabled:opacity-50 dark:hover:bg-white/10"
              style={{ color: 'var(--editor-ink-2)' }}
              disabled={saving}
              onClick={() => api.discardPendingEdits()}
              data-testid="hot-edit-discard"
            >
              Discard
            </button>
            <button
              type="button"
              className="flex h-8 items-center gap-1.5 rounded-[10px] pl-2.5 pr-3 text-xs font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--editor-accent)' }}
              disabled={saving}
              onClick={() =>
                void (save === 'failed' ? api.retryLastSave() : api.applyPendingEdits())
              }
              data-testid="hot-edit-apply"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {saving ? 'Applying…' : save === 'failed' ? 'Try again' : `Apply ${count}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
